import { computed, watch, ref, type Ref, type ComputedRef, type WritableComputedRef } from 'vue';
import { provideLocal, injectLocal } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useProduct } from '~/modules/product/composables/useProduct';
import { useTraits } from '@nuxt-common/media-card-large/app/composables/useTraits';
import { useType as useTypeMCL, useCreateTypeStore } from '@nuxt-common/media-card-large/app/modules/type';
import { useColor as useColorMCL, useInitColor } from '@nuxt-common/media-card-large/app/modules/color';
import { useSize as useSizeMCL, useInitSize, showSizeDropdown } from '@nuxt-common/media-card-large/app/modules/size';
import { useInitUpc, useUpc as useUpcMCL, UpcParser as upcParser } from '@nuxt-common/media-card-large/app/composables/useUpc';
import { useAvailability } from '@nuxt-common/media-card-large/app/composables/useAvailability';
import { useDataStore } from '@nuxt-common/media-card-large/app/composables/useVuexStore';
import { UPC } from '@nuxt-common/media-card-large/app/stores/types';
import { useInitPrice } from '~/modules/price/composables/mcl-utils/useInitPrice';
import { usePriceParser } from '~/modules/price/composables/mcl-utils/usePriceParser';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import isUpcsAvaiable from '~/utils/isMemberProductAvailable';
import { getMemberProduct, getMemberProductIds, getMemberProducts } from '~/utils/product';

import type { BaseColor, Color, BaseSize, Size, ParsedColors, ParsedSizes, ParsedTypes } from '~/declaration/traits';
import { useStickifiedContextData } from '~/modules/sticky-product-traits/composables/useStickifiedContextData';

interface ConsolidatedMemberOrchestrator {
  selectedMember: Ref<number>;
  selectMember: (memberId: number) => void;
  hasSelectedUpcId: ComputedRef<boolean>;
  selectedUpcId: ComputedRef<string | null>;
  isMultiUpc: ComputedRef<boolean>;
  colors: ComputedRef<Color[]>;
  colorProps: ComputedRef<{
    colors: BaseColor[];
    spriteUrls: string[];
    priceToColors: Record<string, string[]>;
    priceToColorsExp: Record<string, string[]>;
  }>;
  selectedColor: WritableComputedRef<Color | null>;
  selectedColorId: ComputedRef<string | null>;
  sizes: ComputedRef<Size[]>;
  sizeProps: ComputedRef<{
    sizes: BaseSize[];
    isSizeDropdown: boolean;
  }>;
  selectedSize: WritableComputedRef<Size | null>;
  selectedSizeId: ComputedRef<string | null>;
  label: ComputedRef<string | undefined>;
  typeLabel: ComputedRef<string | undefined>;
  selectedTypeId: ComputedRef<string | undefined>;
  selectedType: WritableComputedRef<Type | null>;
  types: ComputedRef<Type[]>;
  memberImages: ComputedRef<Record<string, string | undefined>>;
  productTypeName: ComputedRef<string | undefined>;
}

interface Traits {
  color: Record<ProductId, ParsedColors>;
  size: Record<ProductId, ParsedSizes>;
  type: Record<ProductId, ParsedTypes>;
}

export const CONSOLIDATED_MEMBER_ORCHESTRATOR = 'CONSOLIDATED_MEMBER_ORCHESTRATOR';

function toBaseColor(color: Color, availability: Record<string, boolean>): BaseColor {
  const pricingColor = color as unknown as { pricing?: { price?: { priceType?: { onSale?: boolean }; finalPrice?: unknown } } };
  return {
    id: color.id,
    name: color.name,
    spriteOffset: color.swatchImage.swatchSpriteOffset,
    spriteIndex: color.swatchImage.swatchSpriteUrlIndex,
    available: availability[color.id] as boolean,
    onSale: pricingColor?.pricing?.price?.priceType?.onSale || !!pricingColor?.pricing?.price?.finalPrice,
  } as BaseColor;
}

function toBaseSize(size: Size, availability: Record<string, boolean>): BaseSize {
  return {
    id: size.id,
    name: size.name,
    displayName: size.displayName,
    available: !!availability[size.id],
  };
}

export function useConsolidatedMemberProvide() {
  const { productData: product } = useProduct();

  const consolidatedStore = useConsolidatedMemberStore();
  const { getPageDataTraits, getStickifiedContextName } = useStickifiedContextData();
  const { selectedMember } = storeToRefs(consolidatedStore);
  const traits = getPageDataTraits() as unknown as Traits;
  const memberProducts = getMemberProducts(product as Product);
  const memberIds = getMemberProductIds(product as Product);
  const { killswitches } = useKillswitches();

  // When customProductId is provided (isolated context), we must key all trait IDs to prevent collisions with the main PDP
  const selectedMemberId = computed(() => String(getStickifiedContextName(selectedMember)));

  const { selectedColor, selectedColorId, selectedSize, selectedSizeId } = useTraits(selectedMemberId);

  const shouldPropagateColorChange = ref(true);

  const initMemberUpcPrice = (productMap: Array<Product>) => {
    const store = useDataStore(UPC);
    productMap.forEach((product) => {
      const productIdKey = getStickifiedContextName(product.id);
      useInitPrice({ id: productIdKey, data: usePriceParser(product as unknown as Parameters<typeof usePriceParser>[0]) });
      const existingData = store.get?.(productIdKey) as Record<string, unknown> | undefined;
      const shouldPreventOverwrite = killswitches.isBopsUpcAvailabilityUpdateEnabled && existingData?.isBopsUpcAvailabilityUpdateEnabled;
      // Prevent overwriting the store with raw product data if it has already been updated with BOPS availability overrides
      if (!shouldPreventOverwrite) {
        useInitUpc({ id: productIdKey, data: upcParser(product as unknown as Parameters<typeof usePriceParser>[0]) });
      }
    });
  };

  const initMemberTraits = (productMap: Array<Product>) => {
    productMap.forEach((product) => {
      const productIdKey = getStickifiedContextName(product.id);
      if (traits.color[productIdKey]) {
        useInitColor({ id: productIdKey, data: traits.color[productIdKey] as unknown as Parameters<typeof useInitColor>[0]['data'] });
      }
      if (traits.size[productIdKey]) {
        useInitSize({ id: productIdKey, data: traits.size[productIdKey] as unknown as Parameters<typeof useInitSize>[0]['data'] });
      }
      if (traits.type[productIdKey]) {
        useCreateTypeStore({ id: productIdKey, data: traits.type[productIdKey] as unknown as Parameters<typeof useCreateTypeStore>[0]['data'] });
      }
    });
  };

  const selectMember = (memberId: number | string) => {
    shouldPropagateColorChange.value = false;
    consolidatedStore.selectedMember = memberId as number;
  };

  const availableMembers = memberProducts.filter(isUpcsAvaiable);

  initMemberUpcPrice(availableMembers as unknown as Product[]);
  initMemberTraits(availableMembers as unknown as Product[]);

  const { color: colorAvailability, size: sizeAvailability } = useAvailability(selectedMemberId);

  // Color|Size|Type data
  const colors = useColorMCL(selectedMemberId);
  const colorsStore = useDataStore<{
    colorArray?: Color[];
    spriteUrls?: string[];
    priceToColors?: unknown;
    priceToColorsExp?: unknown;
  }>('colors');
  const selectedColorDataStore = useDataStore<Color | null>('selectedColor');

  const { label, sizes } = useSizeMCL(selectedMemberId);
  const sizeStore = useDataStore<{
    sizeArray?: Size[];
  }>('sizes');

  const { label: typeLabel, selectedTypeId, selectedType, types } = useTypeMCL(selectedMemberId);

  // Props
  const colorProps = computed(() => {
    const comp = colorsStore.getComputed(selectedMemberId.value);
    return {
      colors: comp.value?.colorArray?.map((color: Color) => toBaseColor(color, colorAvailability.value)) || [],
      spriteUrls: comp.value?.spriteUrls,
      priceToColors: comp.value?.priceToColors,
      priceToColorsExp: comp.value?.priceToColorsExp,
    };
  });

  const sizeProps = computed(() => {
    const comp = sizeStore.getComputed(selectedMemberId.value);
    return {
      sizes: comp.value?.sizeArray?.map((size: Size) => toBaseSize(size, sizeAvailability.value)) || [],
      isSizeDropdown: showSizeDropdown(comp.value?.sizeArray as unknown as Parameters<typeof showSizeDropdown>[0]),
    };
  });

  const memberImages = computed(() => {
    const map: Record<string, string | undefined> = {};
    for (const productId of memberIds) {
      const selectedColor = selectedColorDataStore.getComputed(productId).value;
      if (selectedColor) {
        map[productId] = selectedColor?.imagery?.images[0]?.filePath;
      }
    }
    return map;
  });

  const productTypeName = computed(() => {
    const typeName = getMemberProduct(product as Product, String(selectedMember.value))?.detail?.typeName;
    return typeName ? typeName.toLowerCase() : undefined;
  });

  watch(selectedColor, (newColor) => {
    if (!shouldPropagateColorChange.value) {
      shouldPropagateColorChange.value = true;
      return;
    }
    const filteredMemberIds = memberIds.filter((id: string) => id !== String(selectedMember.value));
    filteredMemberIds.forEach((id) => {
      const newId = getStickifiedContextName(id);
      const newMemberColorList = traits.color?.[newId]?.colorArray;
      const newColorData = newMemberColorList?.find((c) => c.name === newColor?.name);
      if (newColorData) {
        selectedColorDataStore.set(newId, newColorData);
      }
    });
  });
  // Upc data
  const { hasSelectedUpcId, selectedUpcId, isMultiUpc } = useUpcMCL(selectedMemberId);

  const toProvide = {
    selectedMember,
    selectMember,
    memberImages,
    hasSelectedUpcId,
    isMultiUpc,
    selectedUpcId: selectedUpcId as unknown as ComputedRef<string | null>,
    colors: colors as unknown as ComputedRef<Color[]>,
    colorProps: colorProps as unknown as ComputedRef<{
      colors: BaseColor[];
      spriteUrls: string[];
      priceToColors: Record<string, string[]>;
      priceToColorsExp: Record<string, string[]>;
    }>,
    selectedColor: selectedColor as unknown as WritableComputedRef<Color | null>,
    selectedColorId: selectedColorId as unknown as ComputedRef<string | null>,
    sizes,
    sizeProps,
    selectedSize,
    selectedSizeId: selectedSizeId as unknown as ComputedRef<string | null>,
    label,
    typeLabel,
    selectedTypeId,
    selectedType,
    types,
    productTypeName,
  };

  provideLocal(CONSOLIDATED_MEMBER_ORCHESTRATOR, toProvide);
  return toProvide;
}

export function useConsolidatedMember(): ConsolidatedMemberOrchestrator {
  const consolidatedMember = injectLocal(CONSOLIDATED_MEMBER_ORCHESTRATOR);
  if (!consolidatedMember) {
    throw new Error('`useConsolidatedMember` must be used within a component that has `useConsolidatedMemberProvide` as an ancestor.');
  }
  return consolidatedMember as ConsolidatedMemberOrchestrator;
}
